import React, { useEffect, useState } from 'react';
import { loadKitchenDevice, pinSignIn } from './kitchenDevice';

/**
 * The PIN screen of a registered kitchen tablet (backend/kitchen_devices.py, G-AUTH-2): pick yourself from the site's
 * members who have a PIN, type it, carry on. Used as the device's sign-in page (/kitchen) and as its idle lock, where
 * `currentUserId` starts on the person who was signed in. It is never part of the public sign-in page.
 */
export default function KitchenPinSignIn({ apiUrl = '', onSignedIn, currentUserId = null, onSignOut = null }) {
  const [state, setState] = useState({ status: 'loading' });
  const [chosen, setChosen] = useState(currentUserId);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    loadKitchenDevice(apiUrl).then((r) => {
      if (!alive) return;
      setState(r.ok ? { status: 'ready', ...r.data } : { status: r.reason, message: r.message });
    });
    return () => { alive = false; };
  }, [apiUrl]);

  const submit = async (event) => {
    event.preventDefault();
    if (!chosen || busy || !/^\d{4,6}$/.test(pin)) return;
    setBusy(true);
    setError('');
    const result = await pinSignIn(apiUrl, chosen, pin);
    setBusy(false);
    setPin('');
    if (result.ok) { onSignedIn(result.data); return; }
    if (result.reason === 'unregistered') { setState({ status: 'unregistered', message: result.message }); return; }
    setError(result.message);
  };

  const box = { width: '100%', maxWidth: 420 };
  const button = { width: '100%', padding: 14, borderRadius: 8, fontSize: 18, marginBottom: 8 };
  if (state.status === 'loading') return <p data-testid="kitchen-pin-loading">Loading this kitchen device…</p>;
  if (state.status !== 'ready') {
    return (
      <div style={box} data-testid="kitchen-pin-unregistered">
        <h1 style={{ fontSize: 22, fontWeight: 600 }}>Not a kitchen device</h1>
        <p>{state.message || 'This browser is not registered as a kitchen device.'} Sign in with your password.</p>
        <a href="/login" data-testid="kitchen-pin-password-link">Sign in with your password</a>
      </div>
    );
  }
  const members = state.members || [];
  const who = members.find((m) => m.user_id === chosen);
  return (
    <form onSubmit={submit} style={box} data-testid="kitchen-pin-sign-in">
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Sign in with your PIN</h1>
      <p style={{ opacity: 0.8, marginBottom: 16 }} data-testid="kitchen-pin-device">
        {state.device?.name} · {state.device?.location_name || state.device?.location_id}
      </p>
      {!who && (
        <ul style={{ display: 'grid', gap: 8, marginBottom: 12 }} data-testid="kitchen-pin-members">
          {members.length === 0 && <li data-testid="kitchen-pin-no-members">Nobody here has set a kitchen PIN yet.
            Set one under Settings, Security, with your password.</li>}
          {members.map((m) => (
            <li key={m.user_id}>
              <button type="button" style={{ ...button, border: '1px solid #6b7280' }}
                      data-testid={`kitchen-pin-member-${m.user_id}`} onClick={() => { setChosen(m.user_id); setError(''); }}>
                {m.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {who && (
        <>
          <p style={{ marginBottom: 8 }} data-testid="kitchen-pin-chosen">{who.name}</p>
          <input type="password" inputMode="numeric" autoComplete="off" autoFocus maxLength={6} value={pin}
                 onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} aria-label="PIN"
                 data-testid="kitchen-pin-input"
                 style={{ width: '100%', padding: 14, borderRadius: 8, fontSize: 24, letterSpacing: 8, color: '#111827',
                          marginBottom: 8 }} />
          <button type="submit" disabled={busy || !/^\d{4,6}$/.test(pin)} data-testid="kitchen-pin-submit"
                  style={{ ...button, background: '#2563eb', color: '#fff' }}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <button type="button" data-testid="kitchen-pin-not-me" style={{ ...button, background: 'transparent' }}
                  onClick={() => { setChosen(null); setPin(''); setError(''); }}>
            Not {who.name}?
          </button>
        </>
      )}
      {error && <p role="alert" data-testid="kitchen-pin-error" style={{ color: '#dc2626', marginBottom: 8 }}>{error}</p>}
      {onSignOut && (
        <button type="button" onClick={onSignOut} data-testid="kitchen-pin-sign-out"
                style={{ ...button, background: 'transparent', border: '1px solid #6b7280' }}>
          Sign out
        </button>
      )}
    </form>
  );
}

/** /kitchen: the device's own sign-in page. `onSignedIn` stores the session (App.js completeLogin). */
export function KitchenSignInPage({ apiUrl, onSignedIn }) {
  return (
    <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          data-testid="kitchen-sign-in-page">
      <KitchenPinSignIn apiUrl={apiUrl} onSignedIn={onSignedIn} />
    </main>
  );
}
